import { useEffect } from 'react';

/** Map a key to a page-turn direction (keyboards and Bluetooth page-turn pedals). */
export function pageKeyDirection(key: string, shift: boolean): 1 | -1 | 0 {
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
    case 'PageDown':
      return 1;
    case 'ArrowUp':
    case 'ArrowLeft':
    case 'PageUp':
      return -1;
    case ' ':
    case 'Spacebar':
      return shift ? -1 : 1;
    default:
      return 0;
  }
}

/**
 * Page-turn keys while `active`: ignored in text fields, while a dialog is open, with modifier keys,
 * and Space on a focused button (it activates the button instead). `repeat` = an auto-repeated
 * keydown (key or pedal held down): it still turns pages but must never open anything.
 */
export function usePageKeys(active: boolean, turn: (dir: 1 | -1, repeat: boolean) => void): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const dir = pageKeyDirection(e.key, e.shiftKey);
      if (dir === 0) return;
      if ((e.key === ' ' || e.key === 'Spacebar') && target?.closest('button, [role="button"], [role="tab"], [role="radio"], a')) return;
      e.preventDefault();
      turn(dir, e.repeat);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, turn]);
}
